import { CheckCircle, ChevronRight } from '@mui/icons-material';
import { Box, Divider, Grid } from '@mui/material';
import { Button, Typography } from '@uhc-tempo/components';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { SINGLE_ESTIMATE_ROUTE } from '../../../constants';
import { FormContext } from '../../../context/Form.context';
import formatNumberToCurrency from '../../../utils/NumberUtil';
import { allProviderSpecialties } from '../../../utils/ProviderUtil';
import AverageThresholdTag from './AvgTag.component';
import { checkIndemnityCoverages } from '../../../utils/SearchPageUtil';
import { getMemberPays } from '../../../utils/costBreakdownUtil';

/**
 * Essentially the same as the SingleEstimate component, only to be used
 * next to the map in the MapView component
 *
 * (Slightly different styling with on hover effect, which will be used
 * to determine which provider pin is highlighted (TODO))
 * @param {*} props
 * @returns
 */
const MapEstimate = (props) => {
    const {
        estimate,
        provider,
        setProvider,
        setOtherLocationsOpen,
        setClosestLocationIndex,
        highlightedProvider,
        setHighlightedProvider,
        markerRef,
        setEstimate,
    } = props;

    const [showMapEstimateModal, setShowMapEstimateModal] = useState(false);

    const { setSingleEstimateResponse, memberDetails, formValues } = useContext(FormContext);
    const { selectedService } = formValues;
    const history = useHistory();
    const isIndemnity = checkIndemnityCoverages(memberDetails, true);

    const [highlightedProviderCard, setHighlightedProviderCard] = useState(false);

    const handleOtherLocationsOpen = () => {
        setEstimate(estimate);
        setProvider(provider);
        setOtherLocationsOpen(true);
    };

    const componentRef = useRef();
    const onBeforeGetContentResolve = useRef();

    const handleViewSingleProvider = () => {
        setSingleEstimateResponse(estimate);
        setProvider(provider);
        history.push(SINGLE_ESTIMATE_ROUTE);
    };

    useEffect(() => {
        if (typeof onBeforeGetContentResolve.current === 'function') {
            onBeforeGetContentResolve.current();
        }
    });

    useEffect(() => {
        //Use the map service to figure out the closest/best provider locations based on criteria.
        setClosestLocationIndex([1, 0, 2, 3, 4]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //Will need map service to figure this one out. Does not have to be the closest, just the best option available and then descend.
    // const closestLocationIndex = [2, 1, 3, 4, 5];

    const mouseEnterHandler = () => {
        setHighlightedProvider(provider.prefixId);
    };

    useEffect(() => {
        if (provider.prefixId === highlightedProvider) {
            setHighlightedProviderCard(true);
        } else setHighlightedProviderCard(false);
    }, [highlightedProvider, provider.prefixId]);

    const costShare = estimate?.costShare;
    const geoAverageCost = estimate?.geoAverageCost;
    const costScale = estimate?.costScale;

    return (
        <Grid container>
            <Box
                sx={{
                    width: '100%',
                    borderRadius: '8px',
                    border: highlightedProviderCard
                        ? '4px solid rgba(25, 110, 207, 1)'
                        : '4px solid rgba(0, 0, 0, 0)',
                }}
                data-testid={`estimate-${estimate.id}`}
                ref={componentRef}
                onMouseEnter={mouseEnterHandler}
            >
                <Grid container ref={markerRef}>
                    <Grid
                        item
                        xs={12}
                        style={{
                            paddingTop: '32px',
                            paddingLeft: '20px',
                            paddingBottom: '32px',
                            paddingRight: '20px',
                        }}
                    >
                        <Grid container justifyContent="space-between">
                            <Grid item xs={6}>
                                <Typography.H6
                                    data-testid="provider-name"
                                    style={{ color: '#222222', marginBottom: '0px' }}
                                >
                                    {provider.unTranslatedText}
                                </Typography.H6>
                                <Typography.Paragraph
                                    style={{ color: '#333333', marginBottom: '0px' }}
                                    data-testid="providerSpecialty"
                                >
                                    {allProviderSpecialties(provider?.locAndPractRole)?.length > 0
                                        ? allProviderSpecialties(provider?.locAndPractRole).join(
                                              ', '
                                          )
                                        : 'No Specialties Found'}
                                </Typography.Paragraph>
                            </Grid>
                            <Grid item sx={{ marginBottom: '8px' }}>
                                <Grid container alignItems="flex-end" direction="column">
                                    <Typography.Paragraph
                                        style={{
                                            color: '#5A5A5A',
                                            marginBottom: '0px',
                                            fontWeight: '500',
                                            fontSize: '16px',
                                        }}
                                    >
                                        You pay:
                                    </Typography.Paragraph>
                                    <Typography.Paragraph
                                        data-testid="estimatePrice"
                                        style={{
                                            color: '#333333',
                                            marginBottom: '0px',
                                            fontWeight: '700',
                                            fontSize:
                                                costShare?.outOfPocket ||
                                                costShare?.outOfPocket == 0
                                                    ? '25.63px'
                                                    : '16px',
                                            marginLeft: '8px',
                                        }}
                                    >
                                        {getMemberPays(estimate, 'Not Available')}
                                    </Typography.Paragraph>
                                    {/* <Grid item data-testid="averagePriceThreshold">
                                        {geoAverageCost &&
                                            !isIndemnity[
                                                selectedService?.value?.fullCarePath
                                                    ?.coverageTypeCode
                                            ] && (
                                                <AverageThresholdTag
                                                    costScale={costScale}
                                                    cost={costShare?.outOfPocket}
                                                    currentDialogState={showMapEstimateModal}
                                                    setDialogState={setShowMapEstimateModal}
                                                />
                                            )}
                                    </Grid> */}
                                    {/* //commenting out the average tag for now but will put behind feature flag */}
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography.Paragraph
                                    style={{
                                        color: '#333333',
                                        fontWeight: '500',
                                        fontSize: '14.22px',
                                        marginBottom: '0px',
                                    }}
                                    data-testid="addressLocation"
                                >
                                    {provider?.locAndPractRole?.length > 0
                                        ? provider?.locAndPractRole?.[0]?.singleAddress?.line[0]
                                        : 'street1'}
                                    ,{' '}
                                    {provider?.locAndPractRole?.length > 0
                                        ? provider?.locAndPractRole?.[0]?.singleAddress?.city
                                        : 'city'}
                                    ,{' '}
                                    {provider?.locAndPractRole?.length > 0
                                        ? provider?.locAndPractRole?.[0]?.singleAddress?.state
                                        : 'state'}{' '}
                                    {provider?.locAndPractRole?.length > 0
                                        ? provider?.locAndPractRole?.[0]?.singleAddress?.zipCode
                                        : 'zip'}
                                </Typography.Paragraph>
                                <Typography.Paragraph
                                    style={{
                                        color: '#333333',
                                        marginBottom: '9px',
                                        fontWeight: '500',
                                        fontSize: '14.22px',
                                    }}
                                    data-testid="providerLocationDistance"
                                >
                                    {provider?.locAndPractRole?.length > 0 &&
                                    provider?.locAndPractRole?.[0]?.distance
                                        ? parseFloat(
                                              provider.locAndPractRole?.[0].distance
                                          ).toFixed(2)
                                        : '0.6'}{' '}
                                    Miles Away
                                </Typography.Paragraph>
                                <Grid container alignItems="center" direction="row">
                                    <Typography.Paragraph
                                        role="button"
                                        aria-haspopup="dialog"
                                        onClick={handleOtherLocationsOpen}
                                        onKeyDown={(e) => {
                                            if (e.key == 'Enter' || e.key == ' ') {
                                                handleOtherLocationsOpen();
                                            }
                                        }}
                                        tabIndex="0"
                                        style={{
                                            color: '#196ECF',
                                            fontWeight: '700',
                                            fontSize: '12.64px',
                                            marginBottom: '0px',
                                            cursor: 'pointer',
                                        }}
                                        data-testid="viewOtherLocations"
                                    >
                                        View other locations & costs
                                    </Typography.Paragraph>
                                    <ChevronRight
                                        style={{
                                            color: '#196ECF',
                                            height: '16px',
                                            width: '16px',
                                            cursor: 'pointer',
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item xs={12} style={{ marginTop: '24px' }}>
                                {!isIndemnity[
                                    selectedService?.value?.fullCarePath?.coverageTypeCode
                                ] && (
                                    <Typography.Paragraph
                                        style={{
                                            color: estimate?.claims?.[0]?.procedures?.[0]?.provider
                                                .isInNetwork
                                                ? '#007000'
                                                : '#c40000',
                                            fontWeight: '700',
                                            marginBottom: '4px',
                                        }}
                                        data-testid="provider-tier"
                                    >
                                        {estimate?.claims?.[0]?.procedures?.[0]?.provider
                                            .isInNetwork
                                            ? 'In-Network Provider'
                                            : 'Out of Network Provider'}
                                    </Typography.Paragraph>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container alignItems="center" direction="row">
                                    <CheckCircle
                                        style={{
                                            color: '#007000',
                                            height: '16px',
                                            width: '16px',
                                            marginRight: '8px',
                                        }}
                                    />
                                    <Typography.Paragraph
                                        style={{
                                            color: '#333333',
                                            marginBottom: '0px',
                                            fontWeight: '400',
                                            fontSize: '13px',
                                        }}
                                        data-testid="patientAcceptance"
                                    >
                                        {provider?.acceptingNewPatients?.toLowerCase() === 'true'
                                            ? 'Accepting All Patients'
                                            : 'Accepting Existing Patients'}
                                    </Typography.Paragraph>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} style={{ marginTop: '32px' }}>
                                <Button
                                    buttonType="secondary-one"
                                    onClick={handleViewSingleProvider}
                                    data-testid="selectProviderButton"
                                >
                                    Select provider & view estimate {'>'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
            <Divider />
        </Grid>
    );
};

export default MapEstimate;
